/**
 * The caller's own team memberships.
 *
 * Its own module, small and dependency-light on purpose. The catalog adapter
 * needs it to stamp team ids on an auto-attach, and that adapter sits in the
 * app's entry chunk — importing it from `useTeamRoster` would pull the
 * collaboration and streaming clients in behind it for every page load.
 */
import { useQuery } from "@tanstack/react-query";
import { useSession } from "@authe/ui-core/session";
import { useTeamsApi } from "@/api/adapters";

/** A team the caller belongs to, as a share target names it. */
export interface MyTeam {
  id: string;
  name: string;
}

/**
 * The membership read, with the difference between "no teams" and "didn't load".
 *
 * That distinction is the whole reason this shape exists. Every consumer of the
 * plain list treats it as an ANSWER: the share field offers it as the set of
 * teams you can pick, and `recordVisibility` maps a `team` default with no ids
 * onto a PRIVATE row. So a transient failure used to mean a person kept
 * creating tracers, rules and views they believed were shared with their team
 * and which nobody else could see — and the read that caused it had already
 * thrown its error away (`.catch(() => setTeams([]))`).
 */
export interface MyTeamsRead {
  /** The teams, or the last known list — never emptied by a failure. */
  teams: MyTeam[];
  /** The read is in flight and has never succeeded. */
  isLoading: boolean;
  /** The most recent read failed. `teams` may still hold an earlier answer. */
  failed: boolean;
  /** The failure itself, for a typed error surface. */
  error: unknown;
  /** True when this build has no memberships endpoint wired at all. */
  unavailable: boolean;
  /** Re-run the read. */
  refetch: () => void;
}

export function useMyTeamsRead(): MyTeamsRead {
  const { session } = useSession();
  const teamsApi = useTeamsApi();
  const userId = session?.user.id ?? null;
  const listMemberships = teamsApi.listMemberships;
  // Memberships drive sharing and permissions, not just live channels. Keep
  // them independent of the streaming entitlement and deduplicate consumers.
  // A failed refresh retains only this identity's answer, never another user's.
  const query = useQuery({
    queryKey: ["team-memberships", session?.tenant.customerId, session?.tenant.orgId, session?.tenant.tenantId, userId],
    queryFn: async (): Promise<MyTeam[]> => {
      const memberships = await listMemberships!(userId!);
      return memberships.filter((m) => Boolean(m.team.id))
        .map((m) => ({ id: m.team.id, name: m.team.name || m.team.id }));
    },
    enabled: Boolean(userId && listMemberships),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  return {
    teams: query.data ?? NO_TEAMS,
    isLoading: query.isLoading,
    failed: query.isError,
    error: query.error,
    unavailable: !listMemberships,
    refetch: () => { if (userId && listMemberships) void query.refetch(); },
  };
}

/**
 * Sharing needs these ids even without a live transport. `useTeamIds` uses
 * this same identity-scoped read for permissions and channel subscriptions.
 *
 * Returns the list alone. A caller that WRITES a team-scoped row wants
 * {@link useMyTeamsRead} instead — the list on its own cannot tell an empty
 * answer from a missing one, and treating those the same is what made a failed
 * read privatise a share.
 */
export function useMyTeams(): MyTeam[] {
  return useMyTeamsRead().teams;
}

const NO_TEAMS: MyTeam[] = [];
